<template>
  <div class="play-page">
    <div class="bg-gradient"></div>
    <div class="orb orb-1"></div>
    <div class="orb orb-2"></div>
    <div class="orb orb-3"></div>

    <div class="play-brand-row">
      <div class="play-brand-nav">
        <desktop-brand-corner inline />
        <!-- <router-link class="play-nav-link play-home-link" to="/">首页</router-link> -->
      </div>

      <nav v-if="!isGuestUser" class="play-top-links" aria-label="页面导航">
        <router-link class="play-nav-link" to="/history">
          <font-awesome-icon :icon="['far', 'clock']" class="play-nav-icon" />
          <span>观看记录</span>
        </router-link>
        <router-link class="play-nav-link" :to="{ path: '/history', query: { tab: 'favorites' } }">
          <font-awesome-icon :icon="['far', 'heart']" class="play-nav-icon" />
          <span>我的收藏</span>
        </router-link>
        <router-link class="play-nav-link" to="/myprofile">
          <span class="play-profile-logo" aria-hidden="true">
            <img :src="logo2Image" alt="" class="play-profile-logo-img" />
          </span>
          <span>我的账号</span>
        </router-link>
      </nav>
    </div>

    <main class="page-shell">
      <div class="mobile-topbar" :class="{ 'is-scrolled': isTopbarScrolled }">
        <button
          type="button"
          class="mobile-back-btn"
          @click="handleGoBack"
          aria-label="返回上一页"
        >
          <font-awesome-icon icon="chevron-left" />
        </button>
        <h1 class="mobile-topbar-title">{{ mobileTopbarTitle }}</h1>
        <span class="mobile-topbar-spacer" aria-hidden="true"></span>
      </div>
      <div class="mobile-topbar-offset" aria-hidden="true"></div>

      <div class="play-layout">
        <div class="play-main-column">
          <section class="player-stage" :style="{ aspectRatio: playerAspectRatio }">
            <video
              v-if="episodeVideoUrl"
              :key="episodeVideoUrl"
              ref="videoRef"
              class="player-video"
              :class="{ hidden: shouldHideVideoForCover }"
              :poster="activeEpisode?.coverUrl || ''"
              :muted="isMuted"
              preload="auto"
              controls
              x-webkit-airplay="allow"
              airplay="allow"
              playsinline
              webkit-playsinline
              @loadstart="handleVideoLoadStart"
              @play="handleVideoPlay"
              @pause="handleVideoPause"
              @loadedmetadata="handleLoadedMetadata"
              @seeking="handleVideoSeeking"
              @seeked="handleVideoSeeked"
              @timeupdate="handleTimeUpdate"
              @ended="handleVideoEnded"
              @abort="handleVideoAbort"
              @error="handleVideoError"
            >
              <source :src="episodeVideoUrl" :type="episodeVideoType" />
            </video>

            <div v-if="isPlayTimeLimitReached" class="play-limit-cover">
              <div class="play-limit-card">
                <div class="play-limit-icon">
                  <font-awesome-icon icon="moon" />
                </div>
                <p class="play-limit-text">小朋友，休息时间到了！</p>
                <button type="button" class="parent-unlock-btn" @click="handleParentUnlock">
                  家长解锁
                </button>
              </div>
            </div>

            <div v-else-if="shouldShowVipLimitCover" class="play-limit-cover vip-limit-cover">
              <div class="play-limit-card vip-limit-card">
                <div class="play-limit-icon vip-limit-icon">
                  <font-awesome-icon icon="crown" />
                </div>
                <p class="play-limit-text vip-limit-text">成为会员后可观看完整内容</p>
              </div>
            </div>

            <div v-else-if="videoError" class="player-message">视频加载失败，请稍后重试</div>
          </section>

          <header class="meta-header">
            <section class="content-section">
              <div class="content-title-row">
                <h2 class="section-title">简介</h2>

                <button
                  type="button"
                  class="favorite-season-btn"
                  :class="{ active: isFavorites }"
                  @click="toggleSeasonFavorite"
                >
                  <span class="favorite-season-icon">
                    <font-awesome-icon icon="heart" />
                  </span>
                  <span class="favorite-season-text">收藏</span>
                </button>
              </div>
              <p class="series-desc">{{ activeVideoSeries?.description || '' }}</p>
            </section>
          </header>

          <section class="season-tabs-section">
            <div ref="seasonChipsRef" class="season-tabs">
              <button
                v-for="season in seasons"
                :key="season.id"
                type="button"
                class="season-tab"
                :data-season-id="season.id"
                :class="{ active: activeSeason?.id === season.id }"
                @click="selectSeason(season.id)"
              >
                {{ season.name }}
              </button>
            </div>

            <button type="button" class="menu-toggle-btn" @click="toggleSeasonSheet">
              <font-awesome-icon icon="chevron-down" :class="{ expanded: isSeasonSheetOpen }" />
            </button>
          </section>
        </div>

        <section class="episode-panel">
          <div class="episode-panel-head">
            <h2 class="episode-panel-title">选集</h2>

            <button type="button" class="episode-count-btn" @click="toggleEpisodeRangeSheet">
              <span>{{ filteredEpisodeCountText }}</span>
              <font-awesome-icon icon="chevron-right" />
            </button>
          </div>

        <section class="episode-range-section">
          <div ref="episodeRangeChipsRef" class="episode-range-chips">
            <button
              v-for="range in episodeRanges"
              :key="range.id"
              type="button"
              class="episode-range-chip"
              :data-range-id="range.id"
              :class="{ active: activeEpisodeRange == range.id }"
              @click="selectEpisodeRange(range.id)"
            >
              {{ range.label }}
            </button>
          </div>
        </section>

        <section ref="episodeGridRef" class="episode-grid">
          <article
            v-for="episode in visibleEpisodes"
            :key="episode.id"
            class="episode-card"
            :data-episode-id="episode.id"
            :class="{ active: activeEpisodeId === episode.id }"
            @click="selectEpisode(episode.id)"
          >
          <div
            class="episode-cover"
          >
            <img
              v-if="episode.coverUrl"
              :src="episode.coverUrl"
              class="episode-cover-image"
            />
            <span class="episode-tag" :class="episode.status">
              {{ episode.status === 'learned' ? `已学${episode.learnCount}遍` : '未学' }}
            </span>
          </div>

          <h3 class="episode-title">{{ activeVideoSeries?.name || '' }} {{ activeSeason?.name || '' }} {{ getEpisodeNumber(episode) }}</h3>
          <p class="episode-subtitle">{{ episode.name }}</p>
        </article>
        </section>
        </section>
      </div>
    </main>

    <Transition name="sheet-fade">
      <div v-if="isSeasonSheetOpen" class="sheet-mask" @click="closeSeasonSheet">
        <section class="sheet-panel" @click.stop>
          <h3 class="sheet-title">选择季数</h3>

          <button
            v-for="season in seasons"
            :key="season.id"
            type="button"
            class="sheet-option"
            :class="{ active: activeSeason?.id === season.id }"
            @click="selectSeasonFromSheet(season.id)"
          >
            <span>{{ season.name }}</span>
            <span v-if="activeSeason?.id === season.id" class="sheet-check">已选</span>
          </button>

          <button type="button" class="sheet-cancel" @click="closeSeasonSheet">取消</button>
        </section>
      </div>
    </Transition>

    <Transition name="sheet-fade">
      <div v-if="isEpisodeRangeSheetOpen" class="sheet-mask" @click="closeEpisodeRangeSheet">
        <section class="sheet-panel" @click.stop>
          <h3 class="sheet-title">选择集数</h3>

          <button
            v-for="(episode, index) in filteredEpisodes"
            :key="episode.id"
            type="button"
            class="sheet-option"
            :class="{ active: activeEpisodeId === episode.id }"
            @click="selectEpisodeFromSheet(episode.id)"
          >
            <span>{{ activeVideoSeries.name }} {{ activeSeason.name }} {{ index + 1 }} {{ episode.name }}</span>
            <span v-if="activeEpisodeId === episode.id" class="sheet-check">已选</span>
          </button>

          <button type="button" class="sheet-cancel" @click="closeEpisodeRangeSheet">取消</button>
        </section>
      </div>
    </Transition>

    <Transition name="modal-fade">
      <div
        v-if="isParentUnlockModalOpen"
        class="parent-modal-mask"
        @click="closeParentUnlockModal"
      >
        <section class="parent-modal" @click.stop>
          <button
            type="button"
            class="parent-modal-close"
            aria-label="关闭"
            @click="closeParentUnlockModal"
          >
            <font-awesome-icon icon="xmark" />
          </button>

          <header class="parent-modal-header">
            <h2 class="parent-modal-title">家长模式</h2>
            <p class="parent-modal-subtitle">请输入家长密码后继续</p>
          </header>

          <form class="parent-modal-body" @submit.prevent="submitParentUnlockPassword">
            <div class="parent-lookup-block">
              <label class="parent-lookup-label" for="parent-unlock-password-input">密码</label>
              <div class="parent-input-shell">
                <input
                  id="parent-unlock-password-input"
                  ref="parentPasswordInputRef"
                  v-model="parentPassword"
                  :type="showParentPassword ? 'text' : 'password'"
                  placeholder="请输入密码"
                  class="parent-input"
                  autocomplete="current-password"
                  @input="parentPasswordError = ''"
                />
                <button
                  type="button"
                  class="parent-input-side-btn"
                  :aria-label="showParentPassword ? '隐藏密码' : '显示密码'"
                  :title="showParentPassword ? '隐藏密码' : '显示密码'"
                  @click="showParentPassword = !showParentPassword"
                >
                  <font-awesome-icon :icon="showParentPassword ? 'eye-slash' : 'eye'" />
                </button>
              </div>
              <p v-if="parentPasswordError" class="parent-lookup-error" role="alert">
                {{ parentPasswordError }}
              </p>
            </div>

            <button
              type="submit"
              class="parent-submit-btn"
              :class="{ active: canSubmitParentPassword }"
              :disabled="!canSubmitParentPassword"
            >
              继续观看
            </button>
          </form>
        </section>
      </div>
    </Transition>

    <LoginPromptModal v-model="isLoginPromptOpen" @confirm="goLogin" />
    <VipPromptModal
      v-model="isVipPromptOpen"
      description="成为会员后可观看完整内容"
      @confirm="goVip"
    />
  </div>
</template>

<script setup lang="ts">
import logo2Image from '../assets/logo2.png'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getVideoSeasonsList, getVideoEpisodesList, getVideoSeries, type VideoSeries, VideoSeasons, VideoEpisodes } from '@/api/course-video'
import { getFavorites, addFavorites, deleteFavorites } from '@/api/favorites'
import { addHistory } from '@/api/history'
import { getUser, verifyPassword, updateLearning } from '@/api/user'
import { config } from '@/config/axios/config'
import { getAccessToken } from '@/config/axios/auth'
import LoginPromptModal from '@/components/LoginPromptModal.vue'
import VipPromptModal from '@/components/VipPromptModal.vue'

const route = useRoute()
const router = useRouter()
const PARENT_PLAY_LOCK_STORAGE_KEY = 'tree-sprout-player-parent-play-locked'
const seasons = ref<VideoSeasons[]>([])
const episodes = ref<VideoEpisodes[]>([])
const activeVideoSeries = ref<VideoSeries | null>(null)
const activeSeason = ref<VideoSeasons | null>(null)
const activeEpisode = ref<VideoEpisodes | null>(null)
const activeEpisodeId = ref<number | null>(null)
const activeEpisodeRange = ref('')
const isSeasonSheetOpen = ref(false)
const isEpisodeRangeSheetOpen = ref(false)
const seasonChipsRef = ref(null)
const episodeRangeChipsRef = ref(null)
const episodeGridRef = ref(null)
const videoRef = ref(null)
const isPreviewPlaying = ref(false)
const isMuted = ref(false)
const currentSecond = ref(0)
const videoDuration = ref(0)
const videoError = ref(false)
const playerAspectRatio = ref('16 / 9')
const isTopbarScrolled = ref(false)
const dailyPlayLimitMinutes = ref<number | null>(null)
const watchedTodaySeconds = ref(0)
const isParentUnlocked = ref(false)
const isParentPlayLimitLocked = ref(false)
const isParentUnlockModalOpen = ref(false)
const parentPassword = ref('')
const parentPasswordError = ref('')
const showParentPassword = ref(false)
const parentPasswordInputRef = ref(null)
const isParentPasswordSubmitting = ref(false)
const isFavorites = ref(false)
const isGuestUser = computed(() => getAccessToken() == null)
const isLoginPromptOpen = ref(false)
const isVipPromptOpen = ref(false)
const VIDEO_PREVIEW_LIMIT_SECONDS = 30
const memberPreviewLimitSeconds = ref<number | null>(null)
const memberPreviewWatchedSeconds = ref(0)
const userVipTag = ref(localStorage.getItem('vipTag') === 'true')
const isNonVipUser = computed(() =>
  !isGuestUser.value && userVipTag.value === false
)
const shouldApplyPreviewLimit = computed(() =>
  (isGuestUser.value || isNonVipUser.value) && memberPreviewLimitSeconds.value != null
)
let isVideoSeeking = false
let lastTrackedVideoSecond = 0
let seekStartVideoSecond = 0
let learningUpdateTimer: ReturnType<typeof window.setInterval> | null = null
let isLearningUpdatePending = false

const stopLearningUpdateTimer = () => {
  if (learningUpdateTimer != null) {
    window.clearInterval(learningUpdateTimer)
    learningUpdateTimer = null
  }
}

const sendLearningUpdate = async () => {
  if (isGuestUser.value || isLearningUpdatePending) {
    return
  }

  isLearningUpdatePending = true
  try {
    await updateLearning()
  } catch {
    // Learning progress updates should not interrupt playback.
  } finally {
    isLearningUpdatePending = false
  }
}

const startLearningUpdateTimer = () => {
  stopLearningUpdateTimer()

  if (isGuestUser.value) {
    return
  }

  learningUpdateTimer = window.setInterval(sendLearningUpdate, 60 * 1000)
}

const normalizeMediaUrl = (url?: string) => {
  const rawUrl = url?.trim()
  if (!rawUrl) {
    return ''
  }

  if (rawUrl.startsWith('data:') || rawUrl.startsWith('blob:')) {
    return rawUrl
  }

  try {
    const normalizedUrl = rawUrl.startsWith('//')
      ? new URL(`${window.location.protocol}${rawUrl}`)
      : new URL(rawUrl, config.base_url)

    if (normalizedUrl.protocol === 'http:' && window.location.protocol === 'https:') {
      normalizedUrl.protocol = 'https:'
    }

    return normalizedUrl.href
  } catch {
    return rawUrl
  }
}

const getVideoMimeType = (url: string) => {
  const pathname = url.split('?')[0].split('#')[0].toLowerCase()

  if (pathname.endsWith('.m3u8')) {
    return 'application/vnd.apple.mpegurl'
  }

  if (pathname.endsWith('.mov')) {
    return 'video/quicktime'
  }

  if (pathname.endsWith('.webm')) {
    return 'video/webm'
  }

  return 'video/mp4'
}

const episodeVideoUrl = computed(() => normalizeMediaUrl(activeEpisode.value?.videoUrl))
const episodeVideoType = computed(() => getVideoMimeType(episodeVideoUrl.value))

const filteredEpisodes = computed(() =>
  activeSeason.value
    ? episodes.value.filter((episode) => episode.seasonId === activeSeason.value?.id)
    : []
)

const episodeRanges = computed(() => {
  const groupSize = 15
  const list = filteredEpisodes.value

  return Array.from({ length: Math.ceil(list.length / groupSize) }, (_, index) => {
    const start = index * groupSize
    const items = list.slice(start, start + groupSize)
    const first = start + 1
    const last = start + items.length

    return {
      id: `range-${index + 1}`,
      label: `${first}-${last}`,
      items,
    }
  })
})

const visibleEpisodes = computed(() => {
  const activeRange = episodeRanges.value.find((range) => range.id === activeEpisodeRange.value)
  return activeRange ? activeRange.items : filteredEpisodes.value
})

const getEpisodeNumber = (episode: VideoEpisodes) => {
  const index = filteredEpisodes.value.findIndex((item) => item.id === episode.id)
  return index >= 0 ? index + 1 : ''
}

const filteredEpisodeCountText = computed(() => `共${filteredEpisodes.value.length}集`)

const dailyPlayLimitSeconds = computed(() =>
  dailyPlayLimitMinutes.value == null ? Number.POSITIVE_INFINITY : dailyPlayLimitMinutes.value * 60
)

const hasDailyPlayLimit = computed(() =>
  Number.isFinite(dailyPlayLimitSeconds.value)
)

const canSubmitParentPassword = computed(() =>
  parentPassword.value.trim().length > 0 && !isParentPasswordSubmitting.value
)

const isPlayTimeLimitReached = computed(() =>
  !isGuestUser.value &&
  hasDailyPlayLimit.value &&
  (isParentPlayLimitLocked.value || watchedTodaySeconds.value >= dailyPlayLimitSeconds.value) &&
  !isParentUnlocked.value
)

const isMemberPreviewLimitReached = computed(() =>
  shouldApplyPreviewLimit.value &&
  memberPreviewLimitSeconds.value != null &&
  memberPreviewWatchedSeconds.value >= memberPreviewLimitSeconds.value
)

const shouldShowVipLimitCover = computed(() =>
  isNonVipUser.value && isMemberPreviewLimitReached.value
)

const shouldHideVideoForCover = computed(() =>
  isPlayTimeLimitReached.value || shouldShowVipLimitCover.value
)

const mobileTopbarTitle = computed(() => {
  if (!activeEpisode.value) {
    return ''
  }

  return [activeEpisode.value.name].filter(Boolean).join(' ')
})

const handleLoadedMetadata = () => {
  const video = videoRef.value
  if (!video) {
    return
  }

  videoDuration.value = Number.isFinite(video.duration) ? video.duration : 0
  if (video.videoWidth > 0 && video.videoHeight > 0) {
    playerAspectRatio.value = `${video.videoWidth} / ${video.videoHeight}`
  }

}

const handleVideoLoadStart = () => {
  videoError.value = false

  if (videoRef.value) {
    videoRef.value.muted = false
  }
}

const addWatchedDelta = (watchedDelta, currentVideoSecond) => {
  if (watchedDelta > 0) {
    watchedTodaySeconds.value = Math.min(
      dailyPlayLimitSeconds.value,
      watchedTodaySeconds.value + watchedDelta
    )

    if (shouldApplyPreviewLimit.value) {
      memberPreviewWatchedSeconds.value = Math.min(
        memberPreviewLimitSeconds.value ?? Number.POSITIVE_INFINITY,
        memberPreviewWatchedSeconds.value + watchedDelta
      )
    }
  }

  if (
    !isGuestUser.value &&
    hasDailyPlayLimit.value &&
    watchedTodaySeconds.value >= dailyPlayLimitSeconds.value &&
    !isParentUnlocked.value
  ) {
    isParentPlayLimitLocked.value = true
    window.localStorage.setItem(PARENT_PLAY_LOCK_STORAGE_KEY, '1')
  }

  lastTrackedVideoSecond = currentVideoSecond
  currentSecond.value = currentVideoSecond
}

const trackWatchedVideoTime = (video) => {
  const currentVideoSecond = video.currentTime
  const previousSecond = lastTrackedVideoSecond ?? currentVideoSecond
  const watchedDelta = Math.max(0, currentVideoSecond - previousSecond)

  addWatchedDelta(watchedDelta, currentVideoSecond)
}

const trackSeekedWatchedTime = (video) => {
  const currentVideoSecond = video.currentTime
  const watchedDelta = Math.max(0, currentVideoSecond - seekStartVideoSecond)

  addWatchedDelta(watchedDelta, currentVideoSecond)
}

const enforcePlaybackLimits = () => {
  if (isPlayTimeLimitReached.value) {
    if (!isGuestUser.value && !isParentPlayLimitLocked.value) {
      isParentPlayLimitLocked.value = true
      window.localStorage.setItem(PARENT_PLAY_LOCK_STORAGE_KEY, '1')
    }
    videoRef.value?.pause()
    isPreviewPlaying.value = false
    return true
  }

  return enforcePreviewLimit()
}

const enforcePreviewLimit = () => {
  if (!isMemberPreviewLimitReached.value) {
    return false
  }

  videoRef.value?.pause()
  isPreviewPlaying.value = false

  if (isGuestUser.value) {
    openLoginPrompt()
  } else if (isNonVipUser.value) {
    openVipPrompt()
  }

  return true
}

const handleTimeUpdate = () => {
  const video = videoRef.value
  if (!video) {
    return
  }

  if (isVideoSeeking || video.seeking) {
    currentSecond.value = video.currentTime
    return
  }

  if (enforcePlaybackLimits()) {
    return
  }

  trackWatchedVideoTime(video)

  if (enforcePlaybackLimits()) {
    return
  }

  if (isPlayTimeLimitReached.value) {
    video.pause()
  }
}

const handleVideoPlay = () => {
  if (enforcePlaybackLimits()) {
    return
  }

  lastTrackedVideoSecond = videoRef.value?.currentTime ?? 0
  isPreviewPlaying.value = true
}

const handleVideoPause = () => {
  isPreviewPlaying.value = false
}

const handleVideoSeeking = () => {
  const video = videoRef.value
  if (video) {
    if (!isVideoSeeking) {
      seekStartVideoSecond = lastTrackedVideoSecond ?? video.currentTime
    }

    isVideoSeeking = true
    currentSecond.value = video.currentTime
  }
}

const handleVideoSeeked = () => {
  const video = videoRef.value
  if (video) {
    trackSeekedWatchedTime(video)
    isVideoSeeking = false
    enforcePlaybackLimits()
  }
}

const handleVideoEnded = () => {
  isPreviewPlaying.value = false
}

const handleVideoAbort = () => {
  videoError.value = false
  isPreviewPlaying.value = false
}

const isExpectedMediaAbortError = (reason) => {
  const name = reason?.name || ''
  const code = reason?.code
  const message = [
    reason?.message,
    typeof reason?.toString === 'function' ? reason.toString() : '',
    String(reason || '')
  ].join(' ')

  return (
    name === 'AbortError' ||
    code === 20 ||
    message.includes('fetching process for the media resource was aborted') ||
    message.includes('The play() request was interrupted')
  )
}

const handleVideoError = () => {
  if (videoRef.value?.error?.code === 1) {
    videoError.value = false
    isPreviewPlaying.value = false
    return
  }

  videoError.value = true
  isPreviewPlaying.value = false
}

const handleUnhandledMediaAbortRejection = (event) => {
  if (isExpectedMediaAbortError(event.reason)) {
    event.preventDefault()
    event.stopImmediatePropagation()
  }
}

const handleGlobalMediaAbortError = (event) => {
  if (isExpectedMediaAbortError(event.error || event.message)) {
    event.preventDefault()
    event.stopImmediatePropagation()
  }
}

const syncParentPlayLimitLock = () => {
  if (isGuestUser.value || !hasDailyPlayLimit.value) {
    isParentPlayLimitLocked.value = false
    window.localStorage.removeItem(PARENT_PLAY_LOCK_STORAGE_KEY)
    return
  }

  isParentPlayLimitLocked.value =
    window.localStorage.getItem(PARENT_PLAY_LOCK_STORAGE_KEY) === '1'

  if (isParentPlayLimitLocked.value) {
    watchedTodaySeconds.value = dailyPlayLimitSeconds.value
  }
}

const resetPreview = async () => {
  isPreviewPlaying.value = false
  currentSecond.value = 0
  memberPreviewWatchedSeconds.value = 0
  isVideoSeeking = false
  lastTrackedVideoSecond = 0
  seekStartVideoSecond = 0
  videoError.value = false

  await nextTick()

  const video = videoRef.value
  if (video) {
    video.pause()
    try {
      video.currentTime = 0
    } catch {
      // iOS may reject seeking before metadata is available.
    }
  }
}

const openParentUnlockModal = () => {
  parentPassword.value = ''
  parentPasswordError.value = ''
  showParentPassword.value = false
  isParentUnlockModalOpen.value = true

  nextTick(() => {
    parentPasswordInputRef.value?.focus()
  })
}

const closeParentUnlockModal = () => {
  isParentUnlockModalOpen.value = false
  parentPassword.value = ''
  parentPasswordError.value = ''
  showParentPassword.value = false
  isParentPasswordSubmitting.value = false
}

const handleParentUnlock = () => {
  openParentUnlockModal()
}

const submitParentUnlockPassword = async () => {
  if (!parentPassword.value.trim()) {
    parentPasswordError.value = '请输入密码'
    return
  }

  if (isParentPasswordSubmitting.value) {
    return
  }

  isParentPasswordSubmitting.value = true
  parentPasswordError.value = ''

  try {
    const verified = await verifyPassword(parentPassword.value)
    if (!verified) {
      parentPasswordError.value = '密码不正确，请重新输入'
      return
    }

    isParentPlayLimitLocked.value = false
    watchedTodaySeconds.value = 0
    isParentUnlocked.value = false
    window.localStorage.removeItem(PARENT_PLAY_LOCK_STORAGE_KEY)
    closeParentUnlockModal()
    await nextTick()

    const video = videoRef.value
    if (video) {
      lastTrackedVideoSecond = video.currentTime
      try {
        await video.play()
      } catch {
        isPreviewPlaying.value = false
      }
    }
  } catch {
    parentPasswordError.value = '验证失败，请稍后重试'
  } finally {
    isParentPasswordSubmitting.value = false
  }
}

const syncSeasonChipIntoView = () => {
  nextTick(() => {
    const chipsContainer = seasonChipsRef.value
    if (!chipsContainer) {
      return
    }

    const activeChip = chipsContainer.querySelector(
      `button[data-season-id="${activeSeason.value?.id}"]`
    )
    if (!activeChip) {
      return
    }

    activeChip.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  })
}

const syncActiveEpisodeRange = () => {
  const ranges = episodeRanges.value
  if (!ranges.length) {
    activeEpisodeRange.value = ''
    return
  }

  const matchedRange = ranges.find((range) =>
    range.items.some((episode) => episode.id === activeEpisodeId.value)
  )

  activeEpisodeRange.value = matchedRange ? matchedRange.id : ranges[0].id
}

const syncEpisodeRangeChipIntoView = () => {
  nextTick(() => {
    const chipsContainer = episodeRangeChipsRef.value
    if (!chipsContainer) {
      return
    }

    const activeChip = chipsContainer.querySelector(
      `button[data-range-id="${activeEpisodeRange.value}"]`
    )
    if (!activeChip) {
      return
    }

    activeChip.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  })
}

const syncEpisodeCardIntoView = () => {
  nextTick(() => {
    const grid = episodeGridRef.value
    if (!grid) {
      return
    }

    const activeCard = grid.querySelector(`article[data-episode-id="${activeEpisodeId.value}"]`)
    if (!activeCard) {
      return
    }

    activeCard.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  })
}


const ensureEpisodeInSeason = () => {
  if (!activeSeason.value) {
    return
  }

  const current = episodes.value.find((episode) => episode.id === activeEpisodeId.value)
  if (current && current.seasonId === activeSeason.value.id) {
    return
  }

  const firstEpisode = filteredEpisodes.value[0]
  if (!firstEpisode) {
    return
  }

  activeEpisodeId.value = firstEpisode.id
}

const selectSeason = async (seasonId) => {
  const targetSeason = seasons.value.find((season) => season.id === seasonId)
  if (!targetSeason) {
    return
  }

  activeSeason.value = targetSeason
  const episodesData = await getVideoEpisodesList(route.query.seriesId, targetSeason.id)
  episodes.value = episodesData || []
  const firstEpisode = episodes.value[0] || null
  activeEpisode.value = firstEpisode
  activeEpisodeId.value = firstEpisode?.id || null
  if(!isGuestUser.value) {
    queryFavorite(activeEpisode.value?.id)
    addHistory({contentType: 1, contentId: activeEpisode.value.id})
  }
  syncActiveEpisodeRange()
  resetPreview()
  syncSeasonChipIntoView()
  syncEpisodeRangeChipIntoView()
  syncEpisodeCardIntoView()
}

const selectEpisode = (episodeId) => {
  const target = episodes.value.find((episode) => episode.id === episodeId)
  if (!target) {
    return
  }
  activeEpisodeId.value = target.id
  activeEpisode.value = target
  if(!isGuestUser.value) {
    queryFavorite(activeEpisode.value.id)
    addHistory({contentType: 1, contentId: activeEpisode.value.id})
  }
  syncActiveEpisodeRange()
  resetPreview()
  syncSeasonChipIntoView()
  syncEpisodeRangeChipIntoView()
  syncEpisodeCardIntoView()
}

const selectEpisodeRange = (rangeId) => {
  activeEpisodeRange.value = rangeId
  syncEpisodeRangeChipIntoView()
  syncEpisodeCardIntoView()
}

const toggleSeasonSheet = () => {
  isEpisodeRangeSheetOpen.value = false
  isSeasonSheetOpen.value = !isSeasonSheetOpen.value
}

const closeSeasonSheet = () => {
  isSeasonSheetOpen.value = false
}

const selectSeasonFromSheet = (seasonId) => {
  selectSeason(seasonId)
  closeSeasonSheet()
}

const toggleSeasonFavorite = () => {
  if (!activeEpisode.value) {
    return
  }

  if (isGuestUser.value) {
    openLoginPrompt()
    return
  }

  if (isFavorites.value) {
    deleteFavorites(1, activeEpisode.value.id)
    isFavorites.value = false
  } else {
    addFavorites({contentType: 1, contentId: activeEpisode.value.id})
    isFavorites.value = true
  }
}

const openLoginPrompt = () => {
  isLoginPromptOpen.value = true
}

const openVipPrompt = () => {
  isVipPromptOpen.value = true
}

const goLogin = () => {
  router.push('/login')
}

const goVip = () => {
  isVipPromptOpen.value = false
  router.push('/vip')
}

const toggleEpisodeRangeSheet = () => {
  isSeasonSheetOpen.value = false
  isEpisodeRangeSheetOpen.value = !isEpisodeRangeSheetOpen.value
}

const closeEpisodeRangeSheet = () => {
  isEpisodeRangeSheetOpen.value = false
}

const selectEpisodeFromSheet = (episodeId) => {
  selectEpisode(episodeId)
  closeEpisodeRangeSheet()
}


const scrollPageToTop = () => {
  window.scrollTo(0, 0)
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/videos')
}

const handlePageScroll = () => {
  isTopbarScrolled.value = window.scrollY > 12
}

const queryFavorite = async(episodeId: number) => {
  try {
    const favorites = await getFavorites(1, episodeId)
    isFavorites.value = favorites != null
  } catch {
    
  }
}

const getQueryNumber = (value) => {
  const rawValue = Array.isArray(value) ? value[0] : value
  const numberValue = Number(rawValue)
  return Number.isFinite(numberValue) ? numberValue : null
}

const normalizeDailyPlayLimitMinutes = (value): number | null => {
  if (value == null || value === '' || value === 'unlimited') {
    return null
  }

  const limitMinutes = Number(value)
  return Number.isFinite(limitMinutes) && limitMinutes > 0 ? limitMinutes : null
}

const loadUserPlaybackLimit = async () => {
  if (isGuestUser.value) {
    dailyPlayLimitMinutes.value = null
    memberPreviewLimitSeconds.value = VIDEO_PREVIEW_LIMIT_SECONDS
    return
  }

  try {
    const user = await getUser()
    userVipTag.value = Boolean(user?.vipTag)
    localStorage.setItem('vipTag', String(userVipTag.value))
    dailyPlayLimitMinutes.value = normalizeDailyPlayLimitMinutes(user?.dailyPlayLimitMinutes)
    memberPreviewLimitSeconds.value = userVipTag.value ? null : VIDEO_PREVIEW_LIMIT_SECONDS
  } catch {
    dailyPlayLimitMinutes.value = normalizeDailyPlayLimitMinutes(
      window.localStorage.getItem('tree-sprout-daily-play-limit')
    )
    memberPreviewLimitSeconds.value = isNonVipUser.value ? VIDEO_PREVIEW_LIMIT_SECONDS : null
  }
}

const getSeasonsList = async () => {
  try {
    const seasonsData = await getVideoSeasonsList(route.query.seriesId)
    seasons.value = seasonsData
    const firstSeason = seasons.value[0]
    if (!firstSeason) {
      episodes.value = []
      activeSeason.value = null
      activeEpisodeId.value = null
      return
    }

    const routeSeasonId = getQueryNumber(route.query.seasonId)
    activeSeason.value = seasons.value.find((season) => season.id === routeSeasonId) || firstSeason

    const episodesData = await getVideoEpisodesList(route.query.seriesId, activeSeason.value.id)
    episodes.value = episodesData || []

    const routeEpisodeId = getQueryNumber(route.query.episodesId)
    activeEpisode.value = episodes.value.find((episode) => episode.id === routeEpisodeId) || episodes.value[0] || null
    activeEpisodeId.value = activeEpisode.value?.id || null

    if (activeEpisode.value && !isGuestUser.value) {
      queryFavorite(activeEpisode.value.id)
      addHistory({contentType: 1, contentId: activeEpisode.value.id})
    }

    getVideoSeriesDetail()
    syncActiveEpisodeRange()
    syncSeasonChipIntoView()
    syncEpisodeRangeChipIntoView()
    syncEpisodeCardIntoView()
  } catch {
    
  }
}

const getVideoSeriesDetail = async() => {
  try {
    activeVideoSeries.value = await getVideoSeries(route.query.seriesId)
  } catch {
    
  }
}

onMounted(async () => {
  window.addEventListener('unhandledrejection', handleUnhandledMediaAbortRejection, true)
  window.addEventListener('error', handleGlobalMediaAbortError, true)
  startLearningUpdateTimer()
  await loadUserPlaybackLimit()
  syncParentPlayLimitLock()
  await getSeasonsList()
  scrollPageToTop()
  window.addEventListener('scroll', handlePageScroll, { passive: true })
  handlePageScroll()
})

onUnmounted(() => {
  if (videoRef.value) {
    videoRef.value.pause()
  }
  stopLearningUpdateTimer()
  window.removeEventListener('unhandledrejection', handleUnhandledMediaAbortRejection, true)
  window.removeEventListener('error', handleGlobalMediaAbortError, true)
  window.removeEventListener('scroll', handlePageScroll)
})
</script>

<style scoped>
.play-page {
  min-height: 100vh;
  background: #f9f7f2;
  position: relative;
  isolation: isolate;
  overflow-x: hidden;
  padding: 16px 0 100px;
}

.bg-gradient {
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, #fff8f0 0%, #f9f7f2 50%, #f5f0e8 100%);
  z-index: 0;
  pointer-events: none;
}

.orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.6;
  z-index: 0;
  animation: float 8s ease-in-out infinite;
  pointer-events: none;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #ffe4d4, #ffd4c4);
  top: -150px;
  right: -100px;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #e8f4ff, #d4e8ff);
  bottom: -100px;
  left: -100px;
  animation-delay: -3s;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #ffeaa8, #ffd88a);
  top: 50%;
  left: 30%;
  transform: translateY(-50%);
  animation-delay: -5s;
  opacity: 0.4;
}

@keyframes float {
  0%,
  100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

.play-brand-row {
  position: absolute;
  top: var(--brand-corner-top, 8px);
  left: var(--brand-corner-left, 24px);
  right: var(--brand-corner-right, 24px);
  z-index: 105;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.play-brand-nav,
.play-top-links {
  display: inline-flex;
  align-items: center;
  gap: 18px;
}

.play-top-links {
  justify-content: flex-end;
  gap: 32px;
}

.play-brand-nav :deep(.desktop-brand-icon) {
  width: 68px;
  height: 68px;
}

.play-brand-nav :deep(.desktop-brand-name) {
  font-size: 28px;
}

.play-brand-nav :deep(.desktop-brand-logo) {
  width: 56px;
  height: 56px;
}

.play-brand-nav :deep(.desktop-brand-subtitle) {
  font-size: 15px;
}

.play-nav-link {
  color: #5f5044;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 999px;
  transition:
    background 0.22s ease,
    color 0.22s ease,
    transform 0.22s ease;
}

.play-nav-icon {
  color: #ff8a3d;
  font-size: 14px;
}

.play-profile-logo {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(140, 104, 68, 0.14);
  overflow: hidden;
}

.play-profile-logo-img {
  display: block;
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.play-home-link {
  margin-left: 28px;
}

.play-nav-link:hover {
  background: rgba(255, 138, 61, 0.1);
  color: #cf5c1f;
  transform: translateY(-1px);
}

.page-shell {
  width: calc(100% - 24px);
  margin: 0 0 0 24px;
  position: relative;
  z-index: 1;
  padding-top: 74px;
}

.play-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(420px, 500px);
  align-items: start;
  gap: 24px;
  padding-right: 24px;
}

.play-main-column {
  min-width: 0;
}

.mobile-topbar {
  display: none;
}

.mobile-topbar-offset {
  display: none;
}

.mobile-back-btn {
  display: none;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #5f5044;
  font-size: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.mobile-topbar-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0 12px;
  color: #2d3436;
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-topbar-spacer {
  display: none;
}

.player-stage {
  position: relative;
  width: 100%;
  aspect-ratio: 1.54 / 1;
  border-radius: 10px;
  background: linear-gradient(180deg, #5f6064 0%, #5d5f63 54%, #0c1016 100%);
  overflow: hidden;
}

.player-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  background: #12161d;
  accent-color: #ffffff;
}

.player-video.hidden {
  visibility: hidden;
}

.player-video::-webkit-media-controls-panel {
  background: linear-gradient(180deg, rgba(7, 10, 14, 0) 0%, rgba(7, 10, 14, 0.78) 100%);
}

.player-video::-webkit-media-controls-play-button,
.player-video::-webkit-media-controls-mute-button,
.player-video::-webkit-media-controls-fullscreen-button,
.player-video::-webkit-media-controls-current-time-display,
.player-video::-webkit-media-controls-time-remaining-display {
  filter: brightness(0) invert(1);
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.72);
}

.player-video::-webkit-media-controls-timeline,
.player-video::-webkit-media-controls-volume-slider {
  filter: brightness(1.7) contrast(1.15);
}

.play-limit-cover {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border-radius: inherit;
  background:
    radial-gradient(circle at 50% 35%, rgba(255, 243, 224, 0.2), rgba(255, 243, 224, 0) 34%),
    linear-gradient(180deg, #2f3846 0%, #171d26 100%);
}

.vip-limit-cover {
  background:
    radial-gradient(circle at 50% 35%, rgba(255, 209, 128, 0.22), rgba(255, 209, 128, 0) 36%),
    linear-gradient(180deg, #342f46 0%, #181724 100%);
}

.play-limit-card {
  width: min(100%, 380px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  text-align: center;
  color: #fff;
}

.play-limit-icon {
  width: 76px;
  height: 76px;
  border-radius: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffb36f;
  font-size: 34px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.18);
}

.vip-limit-icon {
  color: #ffd36e;
  background: rgba(255, 211, 110, 0.14);
  border-color: rgba(255, 211, 110, 0.22);
}

.play-limit-text {
  margin: 0;
  font-size: clamp(22px, 3.4vw, 34px);
  line-height: 1.25;
  font-weight: 900;
  letter-spacing: 0;
}

.vip-limit-text {
  max-width: 100%;
  white-space: nowrap;
  font-size: clamp(18px, 3vw, 34px);
}

.parent-unlock-btn {
  min-width: 148px;
  min-height: 46px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff8a3d 0%, #ff6b35 100%);
  color: #fff;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 14px 28px rgba(255, 107, 53, 0.28);
  transition: transform 0.22s ease, box-shadow 0.22s ease;
}

.parent-unlock-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 18px 32px rgba(255, 107, 53, 0.34);
}

.parent-unlock-btn:active {
  transform: translateY(0);
}

.player-message {
  position: absolute;
  left: 50%;
  top: 45%;
  transform: translate(-50%, -50%);
  color: rgba(255, 255, 255, 0.85);
  font-size: clamp(15px, 1.5vw, 22px);
  z-index: 1;
}

.meta-header {
  margin-top: 26px;
}

.play-title {
  margin-top: 24px;
  font-size: clamp(24px, 4vw, 42px);
  line-height: 1.15;
  color: #13161b;
  font-weight: 800;
}

.content-section {
  display: block;
  min-width: 0;
  width: 100%;
}

.content-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}

.favorite-season-btn {
  flex: 0 0 auto;
  min-width: 86px;
  min-height: 44px;
  margin-top: 0;
  padding: 7px 12px;
  border: 1px solid rgba(255, 255, 255, 0.86);
  border-radius: 999px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.82) 0%, rgba(255, 248, 241, 0.74) 100%);
  box-shadow:
    0 14px 30px rgba(92, 77, 64, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.86);
  color: #7a7068;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease,
    border-color 0.22s ease,
    color 0.22s ease,
    background 0.22s ease;
}

.favorite-season-btn:hover {
  transform: translateY(-2px);
  border-color: rgba(255, 182, 145, 0.92);
  box-shadow:
    0 18px 34px rgba(255, 138, 61, 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.92);
  color: #e26d3f;
}

.favorite-season-btn.active {
  border-color: rgba(255, 154, 111, 0.92);
  background:
    linear-gradient(180deg, rgba(255, 241, 235, 0.96) 0%, rgba(255, 229, 217, 0.88) 100%);
  color: #e56d54;
  box-shadow:
    0 18px 34px rgba(255, 138, 61, 0.2),
    0 0 0 1px rgba(255, 154, 111, 0.18);
}

.favorite-season-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.72);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: inherit;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.88);
}

.favorite-season-btn.active .favorite-season-icon {
  background: linear-gradient(135deg, #ffb08d 0%, #ff8a6a 100%);
  color: #fff8f5;
  box-shadow: 0 10px 22px rgba(255, 138, 106, 0.28);
}

.favorite-season-text {
  font-size: 13px;
  font-weight: 800;
  line-height: 1.1;
  text-align: center;
}

.section-title {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  font-size: clamp(17px, 1.55vw, 24px);
  line-height: 1.1;
  font-weight: 800;
  color: #191b20;
}

.section-title::after {
  content: '';
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(180deg, #ffc88e 0%, #ff9b4d 52%, #ff7534 100%);
  box-shadow: 0 5px 14px rgba(255, 123, 52, 0.28), inset 0 1px 1px rgba(255, 255, 255, 0.6);
}

.series-desc {
  margin-top: 14px;
  color: #535861;
  font-size: clamp(14px, 1.15vw, 18px);
  line-height: 1.7;
}

.season-tabs-section {
  margin-top: 28px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.season-tabs {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 16px;
  flex-wrap: nowrap;
  padding: 14px 8px;
  margin: -14px -8px;
  overflow-x: auto;
  overflow-y: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.season-tabs::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.season-tab {
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: #666;
  font-size: clamp(16px, 1.35vw, 22px);
  font-weight: 600;
  min-height: 46px;
  white-space: nowrap;
  padding: 0 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  flex: 0 0 auto;
}

.season-tab:hover {
  background: rgba(255, 255, 255, 0.8);
  transform: translateY(-2px);
}

.season-tab.active {
  border-radius: 999px;
  background: linear-gradient(135deg, #ff8a3d 0%, #ff6b35 100%);
  color: #fff;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.46),
    0 0 16px rgba(255, 184, 108, 0.55),
    0 0 28px rgba(255, 107, 53, 0.34);
}

.menu-toggle-btn {
  width: 56px;
  height: 56px;
  border-radius: 999px;
  border: 1px solid #bebebe;
  background: rgba(239, 239, 239, 0.96);
  color: #26282e;
  font-size: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  flex-shrink: 0;
}

.menu-toggle-btn svg {
  transition: transform 0.25s ease;
}

.menu-toggle-btn .expanded {
  transform: rotate(180deg);
}

.episode-panel {
  min-width: 0;
  max-height: calc(100vh - 96px);
  margin-top: 0;
  padding: 0 0 8px;
  position: sticky;
  top: 74px;
  overflow: hidden;
}

.episode-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.episode-panel-title {
  margin: 0;
  color: #272a31;
  font-size: clamp(18px, 2.2vw, 28px);
  font-weight: 800;
  line-height: 1.1;
}

.episode-count-btn {
  border: none;
  background: transparent;
  color: #98a0ab;
  font-size: clamp(14px, 1.5vw, 22px);
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 0;
}

.episode-range-section {
  margin-top: 16px;
}

.episode-range-chips {
  display: flex;
  gap: 18px;
  padding: 0 0 10px;
  overflow-x: auto;
  overflow-y: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.episode-range-chips::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.episode-range-chip {
  appearance: none;
  -webkit-appearance: none;
  border: none;
  background: transparent;
  color: #8d95a1;
  font-size: clamp(14px, 1.05vw, 18px);
  font-weight: 700;
  white-space: nowrap;
  padding: 0 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.2s ease;
  flex: 0 0 auto;
  position: relative;
}

.episode-range-chip:hover {
  color: #5f82c3;
}

.episode-range-chip.active {
  color: #ff7a36;
}

.episode-range-chip.active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  border-radius: 999px;
  background: #ff7a36;
}

.episode-grid {
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  max-height: calc(100vh - 210px);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 4px 0 14px;
  scroll-snap-type: y proximity;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.episode-grid::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.episode-card {
  min-width: 0;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: transform 0.2s ease;
  scroll-snap-align: start;
}

.episode-card:hover {
  transform: translateY(-2px);
}

.episode-cover {
  position: relative;
  width: 100%;
  aspect-ratio: 1.55 / 1;
  border-radius: 14px;
  background: linear-gradient(145deg, var(--cover-start), var(--cover-end));
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.episode-card.active .episode-cover::after {
  content: '';
  position: absolute;
  inset: 0;
  border: 2px solid #ff7a36;
  border-radius: inherit;
  box-sizing: border-box;
  pointer-events: none;
  z-index: 2;
}

.episode-cover-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.cover-icon {
  color: #8397a3;
  font-size: 20px;
}

.episode-tag {
  position: absolute;
  right: 8px;
  bottom: 8px;
  min-width: 52px;
  padding: 4px 8px;
  border-radius: 999px;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.92);
}

.episode-tag.learned {
  color: #4b9e1e;
}

.episode-tag.unlearned {
  color: #ff8a3d;
}

.episode-title {
  margin-top: 10px;
  color: #272b33;
  font-size: clamp(13px, 0.95vw, 16px);
  font-weight: 700;
  line-height: 1.28;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;
}

.episode-subtitle {
  margin-top: 4px;
  color: #7e8691;
  font-size: clamp(12px, 0.85vw, 14px);
  font-weight: 600;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.sheet-mask {
  position: fixed;
  inset: 0;
  z-index: 30;
  background: rgba(17, 21, 26, 0.34);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0 12px 12px;
}

.sheet-panel {
  width: min(720px, 100%);
  max-height: min(78vh, 920px);
  border-radius: 24px;
  border: 1px solid rgba(221, 214, 202, 0.95);
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 -12px 38px rgba(24, 25, 26, 0.16);
  padding: 10px 14px 14px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.sheet-title {
  margin: 0 0 12px;
  text-align: center;
  font-size: 16px;
  color: #3b3b3b;
  font-weight: 700;
}

.sheet-option {
  width: 100%;
  border: 1px solid #e6e0d8;
  border-radius: 14px;
  background: #f8f6f2;
  color: #3f3f3f;
  font-size: 15px;
  font-weight: 600;
  padding: 13px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.25s ease;
}

.sheet-option + .sheet-option {
  margin-top: 10px;
}

.sheet-option.active {
  border-color: #ffb788;
  background: linear-gradient(135deg, #ffeddc 0%, #ffe3d0 100%);
  color: #cf5c1f;
}

.sheet-check {
  font-size: 13px;
  font-weight: 700;
}

.sheet-cancel {
  width: 100%;
  margin-top: 12px;
  border: none;
  border-radius: 14px;
  background: #ece7de;
  color: #4c4c4c;
  font-size: 15px;
  font-weight: 700;
  padding: 13px 14px;
  cursor: pointer;
}

.sheet-fade-enter-active,
.sheet-fade-leave-active {
  transition: opacity 0.25s ease;
}

.sheet-fade-enter-from,
.sheet-fade-leave-to {
  opacity: 0;
}

.sheet-fade-enter-active .sheet-panel,
.sheet-fade-leave-active .sheet-panel {
  transition: transform 0.25s ease;
}

.sheet-fade-enter-from .sheet-panel,
.sheet-fade-leave-to .sheet-panel {
  transform: translateY(100%);
}

.parent-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(52, 42, 34, 0.36);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.parent-modal {
  position: relative;
  width: min(560px, 100%);
  border-radius: 34px;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(255, 251, 246, 0.98) 0%, rgba(249, 241, 231, 0.97) 100%);
  border: 1px solid rgba(255, 184, 136, 0.28);
  box-shadow:
    0 30px 60px rgba(140, 104, 68, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

.parent-modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.56);
  color: #8f7b6a;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.parent-modal-close:hover {
  background: rgba(255, 255, 255, 0.84);
  color: #6b594a;
}

.parent-modal-header {
  padding: 34px 28px 22px;
  text-align: center;
  border-bottom: 1px solid rgba(186, 159, 133, 0.24);
}

.parent-modal-title {
  margin: 0;
  font-size: 28px;
  line-height: 1.1;
  color: #3f342c;
  font-weight: 800;
}

.parent-modal-subtitle {
  margin: 10px 0 0;
  color: #8d7c6d;
  font-size: 15px;
  font-weight: 700;
}

.parent-modal-body {
  padding: 28px;
}

.parent-lookup-label {
  display: block;
  margin-bottom: 10px;
  color: #5f5044;
  font-size: 15px;
  font-weight: 800;
}

.parent-input-shell {
  min-height: 64px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.74);
  border: 1px solid rgba(186, 159, 133, 0.28);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
  padding: 0 14px 0 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.parent-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: #4f4137;
  font-size: 18px;
  outline: none;
}

.parent-input::placeholder {
  color: #ab9b8d;
}

.parent-input-side-btn {
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: #a18f80;
  cursor: pointer;
}

.parent-input-side-btn:hover {
  background: rgba(255, 255, 255, 0.55);
  color: #6f6054;
}

.parent-lookup-error {
  margin: 10px 0 0;
  color: #d84f3d;
  font-size: 14px;
  font-weight: 700;
}

.parent-submit-btn {
  width: 100%;
  min-height: 70px;
  margin-top: 24px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(180deg, #e6dfd6 0%, #d8cfc4 100%);
  color: rgba(50, 42, 35, 0.72);
  font-size: 20px;
  font-weight: 800;
  cursor: not-allowed;
  box-shadow: 0 12px 24px rgba(153, 120, 87, 0.08);
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    background 0.25s ease,
    color 0.25s ease;
}

.parent-submit-btn.active {
  background: linear-gradient(135deg, #ff8a3d 0%, #ff6b35 100%);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 12px 24px rgba(255, 138, 61, 0.24);
}

.parent-submit-btn.active:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 28px rgba(255, 138, 61, 0.32);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 980px) {
  .page-shell {
    width: calc(100% - 28px);
    margin: 0 auto;
    padding-top: 64px;
  }

  .play-layout {
    display: block;
    padding-right: 0;
  }

  .episode-panel {
    max-height: none;
    margin-top: 30px;
    padding: 24px 0 8px;
    position: static;
    overflow: visible;
  }

  .episode-range-chips {
    gap: 30px;
  }

  .episode-range-chip {
    font-size: clamp(16px, 1.7vw, 24px);
  }

  .episode-grid {
    display: flex;
    gap: 16px;
    max-height: none;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x proximity;
  }

  .favorite-season-btn {
    min-width: 84px;
    min-height: 42px;
    padding: 7px 11px;
  }

  .season-tab {
    min-height: 50px;
    padding: 0 28px;
    font-size: 18px;
  }

  .menu-toggle-btn {
    width: 50px;
    height: 50px;
    font-size: 18px;
  }

  .episode-card {
    flex: 0 0 220px;
  }
}

@media (max-width: 768px),
  (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 540px) {
  .play-brand-row {
    display: none;
  }

  .page-shell {
    width: calc(100% - 32px);
    padding-top: 0;
  }

  .content-section {
    width: 100%;
  }

  .favorite-season-btn {
    min-width: 80px;
    min-height: 40px;
    margin-top: 0;
    padding: 6px 10px;
    border-radius: 999px;
    gap: 5px;
  }

  .favorite-season-icon {
    width: 22px;
    height: 22px;
    font-size: 12px;
  }

  .favorite-season-text {
    font-size: 12px;
  }

  .mobile-topbar {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    min-height: 56px;
    padding: calc(env(safe-area-inset-top, 0px) + 6px) 16px 6px;
    z-index: 20;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }

  .mobile-topbar.is-scrolled {
    background: rgba(249, 247, 242, 0.96);
    box-shadow: 0 8px 20px rgba(45, 52, 54, 0.08);
  }

  .mobile-topbar-offset {
    display: block;
    height: calc(env(safe-area-inset-top, 0px) + 56px);
  }

  .mobile-back-btn {
    display: inline-flex;
    position: absolute;
    left: 16px;
    top: calc(env(safe-area-inset-top, 0px) + 6px);
    transform: none;
  }

  .play-limit-cover {
    padding: 16px;
  }

  .play-limit-card {
    gap: 12px;
  }

  .play-limit-icon {
    width: 58px;
    height: 58px;
    border-radius: 22px;
    font-size: 26px;
  }

  .play-limit-text {
    font-size: 20px;
  }

  .parent-unlock-btn {
    min-width: 126px;
    min-height: 38px;
    font-size: 14px;
  }
}

@media (max-width: 680px) {
  .play-page {
    padding-top: 10px;
  }

  .content-section {
    width: 100%;
  }

  .section-title::after {
    height: 5px;
  }

  .favorite-season-btn {
    min-width: 76px;
    min-height: 38px;
    margin-top: 0;
    padding: 6px 9px;
    gap: 5px;
  }

  .episode-panel {
    margin-top: 24px;
    padding-bottom: 8px;
  }

  .season-tabs {
    gap: 10px;
  }

  .season-tab {
    min-height: 42px;
    padding: 0 20px;
    font-size: 18px;
  }

  .menu-toggle-btn {
    width: 42px;
    height: 42px;
    font-size: 13px;
  }

  .episode-range-chips {
    gap: 18px;
  }

  .episode-range-chip {
    font-size: 18px;
  }

  .episode-grid {
    gap: 12px;
  }

  .episode-card {
    flex: 0 0 176px;
  }

  .sheet-mask {
    padding: 0;
  }

  .sheet-panel {
    width: 100%;
    max-height: 82vh;
    border-radius: 22px 22px 0 0;
    border-bottom: none;
    padding: 10px 12px 16px;
  }
}

@media (max-width: 480px) {
  .page-shell {
    width: calc(100% - 28px);
  }

  .content-section {
    width: 100%;
  }

  .mobile-topbar {
    padding-left: 14px;
    padding-right: 14px;
  }

  .mobile-back-btn {
    left: 14px;
  }

  .mobile-topbar-title {
    font-size: 18px;
  }

  .favorite-season-btn {
    min-width: 72px;
    min-height: 36px;
    padding: 5px 8px;
    border-radius: 999px;
  }

  .favorite-season-icon {
    width: 21px;
    height: 21px;
    font-size: 11px;
  }

  .play-limit-card {
    gap: 10px;
  }

  .play-limit-icon {
    width: 52px;
    height: 52px;
    border-radius: 20px;
    font-size: 23px;
  }

  .play-limit-text {
    font-size: 18px;
  }

  .parent-unlock-btn {
    min-width: 116px;
    min-height: 36px;
    font-size: 13px;
  }

  .favorite-season-text {
    font-size: 11px;
  }
}

@media (max-width: 768px),
  (min-width: 769px) and (max-width: 1024px) and (orientation: portrait),
  (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 540px) {
  .orb {
    display: none;
  }

  .mobile-back-btn,
  .filter-chip,
  .sheet-panel,
  .favorite-season-btn {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  .filter-chip,
  .sheet-option,
  .favorite-season-btn,
  .parent-unlock-btn {
    transition-property: background-color, color, border-color, box-shadow, transform, opacity;
  }
}
</style>
